import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import AlertsBanner from '../../components/BridgeClub/AlertsBanner';
import FeedStatusCard from '../../components/BridgeClub/FeedStatusCard';
import SubscriptionCard from '../../components/BridgeClub/SubscriptionCard';
import FeedCalculator from '../../components/BridgeClub/FeedCalculator';
import WeatherWidget from '../../components/BridgeClub/WeatherWidget';
import FeedbackBox from '../../components/BridgeClub/FeedbackBox';
import WelcomePopup from '../../components/BridgeClub/WelcomePopup';
import RecoverPurchasesModal from '../../components/BridgeClub/RecoverPurchasesModal';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './BridgeClubDashboard.css';

// Generate greeting based on LOCAL browser time
const getGreeting = (firstName: string): string => {
  // new Date().getHours() returns LOCAL time (0-23)
  // Do NOT use getUTCHours() - that would give UTC time
  const hour = new Date().getHours();
  const name = firstName || 'there';

  if (hour >= 5 && hour < 12) {
    // Morning (5am - 11:59am local)
    const greetings = [
      `Good morning, ${name}!`,
      `Morning, ${name}!`,
      `Rise and shine, ${name}!`,
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  } else if (hour >= 12 && hour < 17) {
    // Afternoon (12pm - 4:59pm local)
    const greetings = [
      `Good afternoon, ${name}!`,
      `Hey there, ${name}!`,
      `Afternoon, ${name}!`,
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  } else if (hour >= 17 && hour < 21) {
    // Evening (5pm - 8:59pm local)
    const greetings = [
      `Good evening, ${name}!`,
      `Evening, ${name}!`,
      `Hey ${name}!`,
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  } else {
    // Night (9pm - 4:59am local)
    const greetings = [
      `Burning the midnight oil, ${name}?`,
      `Hey ${name}, working late?`,
      `Evening, ${name}!`,
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  }
};

interface DashboardData {
  member: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    profile_picture: string | null;
    loyalty_progress: number;
    bags_until_reward: number;
    free_bags_available: number;
    total_purchases: number;
    welcome_popup_seen: boolean;
  };
  alerts: {
    weather: any[];
    low_feed: any[];
  };
  feed_bins: any[];
  animals: {
    summary: any[];
    total_groups: number;
    total_daily_consumption_lbs: number;
  };
  farm_challenge: {
    participating: boolean;
    current_balance?: number;
    current_streak_weeks?: number;
    multiplier?: number;
    base_points?: number;
    last_activity_date?: string | null;
  };
  home_store: {
    id: number;
    name: string;
    city: string;
    state: string;
    address: string;
    zip: string;
    logo_url: string | null;
    logo_background_color: string;
  } | null;
  store_promotion: {
    id: number;
    title: string;
    message: string;
    image_url: string | null;
    start_date: string;
    end_date: string;
  } | null;
  hank_tip: string | null;
  conversion_banner?: {
    converted_at: string;
    transactions_migrated: number;
    retroactive_spend: number;
    retroactive_points: number;
    rewards_available: number;
    credit_value_dollars: number;
    points_to_next_reward: number;
    days_since_conversion: number;
  } | null;
  recover_purchases_prompt?: {
    should_show: boolean;
    reason: 'first_login' | 'profile_link' | 'not_applicable';
    has_existing_history: boolean;
  } | null;
}

const BridgeClubDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [greeting, setGreeting] = useState<string>('');
  const [showPromoImageModal, setShowPromoImageModal] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  // Recover Past Purchases — local mill members linking shadow-profile
  // UnmatchedVisitor history at first login. Banner visibility comes
  // from the dashboard payload; the modal opens on banner click + on
  // the profile-page link.
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const [recoverBannerHidden, setRecoverBannerHidden] = useState(false);
  const { hasLocalAccess } = useLocalAccess();

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Show welcome popup for first-time users
  useEffect(() => {
    if (dashboard && !dashboard.member.welcome_popup_seen) {
      setShowWelcomePopup(true);
    }
  }, [dashboard]);

  // Set greeting when member data is loaded
  useEffect(() => {
    if (dashboard?.member?.first_name) {
      setGreeting(getGreeting(dashboard.member.first_name));
    }
  }, [dashboard?.member?.first_name]);

  // Handle Escape key to close promo image modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPromoImageModal) {
        setShowPromoImageModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showPromoImageModal]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getDashboard();
      setDashboard(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="bridge-club-loading">
          <div className="bridge-club-spinner"></div>
          <p>Loading...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error || !dashboard) {
    return (
      <BridgeClubLayout>
        <div className="bridge-club-error">
          <p>{error || 'Something went wrong'}</p>
          <button onClick={loadDashboardData}>Try Again</button>
        </div>
      </BridgeClubLayout>
    );
  }

  const hasWeatherAlerts = dashboard.alerts.weather.length > 0;

  const handleWelcomeClose = (markAsSeen: boolean) => {
    setShowWelcomePopup(false);
    if (markAsSeen && dashboard) {
      // Update local state so it doesn't show again this session
      setDashboard({
        ...dashboard,
        member: { ...dashboard.member, welcome_popup_seen: true }
      });
    }
  };

  // Recover Past Purchases — dismiss handler (fires backend update,
  // hides banner locally without a full dashboard reload).
  const handleDismissRecoverBanner = async () => {
    setRecoverBannerHidden(true);
    try {
      await bridgeClubAPI.dismissRecoverPrompt();
    } catch (err) {
      // Best-effort — banner stays hidden this session even on error.
      console.warn('Failed to dismiss recover prompt:', err);
    }
  };

  // Fires after the modal completes (any outcome). Backend has flipped
  // state to 'completed'; we hide the banner locally.
  const handleRecoverCompleted = () => {
    setRecoverBannerHidden(true);
  };

  const recoverBannerVisible = Boolean(
    dashboard?.recover_purchases_prompt?.should_show && !recoverBannerHidden,
  );

  return (
    <BridgeClubLayout>
      {/* Welcome Popup for first-time users */}
      <WelcomePopup isOpen={showWelcomePopup} onClose={handleWelcomeClose} />

      {/* Recover Past Purchases modal — opens from the dashboard
          banner below AND from the profile-page link. Same component. */}
      <RecoverPurchasesModal
        isOpen={showRecoverModal}
        onClose={() => setShowRecoverModal(false)}
        onCompleted={handleRecoverCompleted}
      />

      <div className="bridge-club-dashboard">
        {/* Weather Alerts Banner - FIRST element, in normal document flow */}
        {hasWeatherAlerts && (
          <div className="dashboard-alerts-container">
            <AlertsBanner
              weatherAlerts={dashboard.alerts.weather}
            />
          </div>
        )}

        {/* Recover Past Purchases banner — first-login affordance for
            local mill members who self-registered with prior
            UnmatchedVisitor shadow profiles. Dismissible. Backend
            controls visibility via the recover_purchases_state. */}
        {recoverBannerVisible && (
          <div style={{
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            border: '1px solid #10B981',
            borderRadius: 12,
            padding: 16,
            margin: '0 0 16px 0',
            color: '#065F46',
            position: 'relative',
          }}>
            <button
              onClick={handleDismissRecoverBanner}
              aria-label="Dismiss"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'none',
                border: 'none',
                fontSize: 20,
                color: '#065F46',
                cursor: 'pointer',
                padding: '0 6px',
              }}
            >
              ×
            </button>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, paddingRight: 24 }}>
              🛒 Have you shopped at Red Bridge before?
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
              Link your past purchases to your new account! Your visit
              history and points will transfer automatically.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowRecoverModal(true)}
                style={{
                  padding: '8px 16px',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Link my purchases
              </button>
              <button
                onClick={handleDismissRecoverBanner}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: '#065F46',
                  border: '1px solid #10B981',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Maybe later
              </button>
            </div>
          </div>
        )}

        {/* Walk-in → member conversion welcome banner. Active for 30 days
            after the WalkInConversionLog is created so the member can see
            what came with them: migrated visits, starting points, and any
            store credit already unlocked. */}
        {dashboard.conversion_banner && (
          <div style={{
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: '1px solid #F59E0B',
            borderRadius: 12,
            padding: 16,
            margin: '0 0 16px 0',
            color: '#78350F',
          }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
              Welcome back!
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
              We found <strong>{dashboard.conversion_banner.transactions_migrated} past visit{dashboard.conversion_banner.transactions_migrated === 1 ? '' : 's'}</strong>{' '}
              totaling <strong>${dashboard.conversion_banner.retroactive_spend.toFixed(2)}</strong>.{' '}
              You started with <strong>{dashboard.conversion_banner.retroactive_points} points</strong>
              {dashboard.conversion_banner.rewards_available > 0 ? (
                <>
                  {' '}— you already have <strong>${dashboard.conversion_banner.credit_value_dollars} in store credit</strong>{' '}
                  ready to use ({dashboard.conversion_banner.rewards_available} reward
                  {dashboard.conversion_banner.rewards_available === 1 ? '' : 's'}).
                </>
              ) : (
                <>
                  {' '}— <strong>{dashboard.conversion_banner.points_to_next_reward} points</strong> away from your next $10 reward.
                </>
              )}
            </div>
          </div>
        )}

        {/* Welcome with Profile Picture */}
        <div className="welcome-section">
          <div className="welcome-header">
            {dashboard.member.profile_picture ? (
              <img
                src={dashboard.member.profile_picture}
                alt={dashboard.member.first_name}
                className="welcome-profile-pic"
              />
            ) : (
              <div className="welcome-profile-initials">
                {dashboard.member.first_name?.charAt(0) || ''}
                {dashboard.member.last_name?.charAt(0) || ''}
              </div>
            )}
            <div className="welcome-text">
              <h2 className="welcome-greeting">{greeting}</h2>
              <p className="welcome-subtitle">Here's your homestead report</p>
            </div>
          </div>
        </div>

        {/* Home delivery - renders only for delivery subscribers */}
        <SubscriptionCard />

        {/* Mill Member Banner - Only for local access members */}
        {hasLocalAccess && (
          <div className="mill-member-banner">
            <div className="mill-member-header">
              <span className="mill-member-badge">Mill Member</span>
              <span className="mill-member-title">Shop the Mill Store</span>
            </div>
            <div className="mill-member-tiles">
              <Link to="/bridge-club/scan-and-go" className="mill-tile scan-go-tile">
                <span className="mill-tile-icon">📱</span>
                <span className="mill-tile-label">Scan & Go</span>
              </Link>
              <Link to="/bridge-club/shop" className="mill-tile">
                <span className="mill-tile-icon">🛒</span>
                <span className="mill-tile-label">Shop</span>
              </Link>
              <Link to="/bridge-club/byoc" className="mill-tile">
                <span className="mill-tile-icon">🌾</span>
                <span className="mill-tile-label">BYOC</span>
              </Link>
              <Link to="/bridge-club/local-orders" className="mill-tile">
                <span className="mill-tile-icon">📋</span>
                <span className="mill-tile-label">Orders</span>
              </Link>
            </div>
          </div>
        )}

        {/* Section 2: Rewards Summary (Compact, side by side) - Hidden for local access members (they get wholesale pricing) */}
        {!hasLocalAccess && (
          <Link to="/bridge-club/card" className="rewards-compact">
            <div className="rewards-left">
              <span className="rewards-emoji">🎁</span>
              <div className="rewards-info">
                <span className="rewards-number">{dashboard.member.free_bags_available}</span>
                <span className="rewards-label">FREE {dashboard.member.free_bags_available === 1 ? 'BAG' : 'BAGS'}</span>
              </div>
              {dashboard.member.free_bags_available > 0 && (
                <span className="rewards-badge">Redeem</span>
              )}
            </div>
            <div className="rewards-divider" />
            <div className="rewards-right">
              <span className="progress-label">🎯 {dashboard.member.loyalty_progress}/10 to next</span>
              <div className="progress-bar-mini">
                <div
                  className="progress-fill-mini"
                  style={{ width: `${(dashboard.member.loyalty_progress / 10) * 100}%` }}
                />
              </div>
            </div>
          </Link>
        )}

        {/* Section 3: Feed Status */}
        <FeedStatusCard bins={dashboard.feed_bins} />

        {/* Section 4: Weather */}
        <WeatherWidget />

        {/* Section 5: Feed Calculator */}
        <FeedCalculator />

        {/* Section 6: Quick Actions */}
        <div className="quick-actions-compact">
          <h2>Quick Actions</h2>
          <div className="actions-grid-compact actions-row">
            <Link to="/bridge-club/upload" className="action-tile">
              <span className="action-tile-icon">📷</span>
              <span className="action-tile-label">Scan Receipt</span>
            </Link>
            <Link to="/bridge-club/purchases" className="action-tile">
              <span className="action-tile-icon">📦</span>
              <span className="action-tile-label">Purchases</span>
            </Link>
            <Link to="/bridge-club/farm-challenge" className="action-tile">
              <span className="action-tile-icon">🏆</span>
              <span className="action-tile-label">Farm Challenge</span>
            </Link>
          </div>
        </div>

        {/* Section 6.5: Resources */}
        <Link to="/bridge-club/resources" className="resources-card">
          <div className="resources-card-icon">📚</div>
          <div className="resources-card-content">
            <h3>Resources</h3>
            <p>Videos, guides, and helpful materials</p>
          </div>
          <span className="resources-card-arrow">→</span>
        </Link>

        {/* Section 7: My Store (compact) with logo and directions */}
        {dashboard.home_store ? (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
              [
                dashboard.home_store.address,
                dashboard.home_store.city,
                `${dashboard.home_store.state} ${dashboard.home_store.zip}`
              ].filter(Boolean).join(', ')
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="store-compact store-compact-clickable"
          >
            {dashboard.home_store.logo_url ? (
              <div
                className="store-logo-container"
                style={{ backgroundColor: dashboard.home_store.logo_background_color }}
              >
                <img
                  src={dashboard.home_store.logo_url}
                  alt={dashboard.home_store.name}
                  className="store-logo"
                />
              </div>
            ) : (
              <span className="store-emoji">🏪</span>
            )}
            <div className="store-info-compact">
              <span className="store-name-compact">{dashboard.home_store.name}</span>
              <span className="store-location-compact">
                {dashboard.home_store.city}, {dashboard.home_store.state}
              </span>
            </div>
            <span className="store-directions-icon">📍</span>
          </a>
        ) : (
          <Link to="/bridge-club/find-store" className="store-link-card">
            <span className="store-emoji">📍</span>
            <span className="store-link-text">Link your home store</span>
            <span className="store-link-arrow">→</span>
          </Link>
        )}

        {/* Section 8: Store Promotion */}
        {dashboard.store_promotion && (
          <div className="promo-card">
            {dashboard.store_promotion.image_url && (
              <div
                className="promo-image-container"
                onClick={() => setShowPromoImageModal(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setShowPromoImageModal(true)}
              >
                <img
                  src={dashboard.store_promotion.image_url}
                  alt={dashboard.store_promotion.title}
                  className="promo-image"
                />
                <div className="promo-image-zoom-hint">
                  <span className="zoom-icon">🔍</span>
                  <span className="zoom-text">Tap to view</span>
                </div>
              </div>
            )}
            <div className="promo-content">
              <span className="promo-badge">Store Special</span>
              <h3 className="promo-title">{dashboard.store_promotion.title}</h3>
              <p className="promo-message">{dashboard.store_promotion.message}</p>
            </div>
          </div>
        )}

        {/* Section 9: Hank's Tip */}
        <div className="tip-compact">
          <span className="tip-avatar-compact">👨‍🌾</span>
          <div className="tip-content-compact">
            <span className="tip-author-compact">Tip from Hank</span>
            <p className="tip-text-compact">
              "{dashboard.hank_tip || 'Keep feed stored in a cool, dry place. Happy animals start with quality feed!'}"
            </p>
          </div>
        </div>

        {/* Section 9: Feedback Box */}
        <FeedbackBox memberEmail={dashboard.member.email} />

      </div>

      {/* Promotion Image Modal */}
      {showPromoImageModal && dashboard.store_promotion?.image_url && (
        <div
          className="promo-image-modal-overlay"
          onClick={() => setShowPromoImageModal(false)}
          onKeyDown={(e) => e.key === 'Escape' && setShowPromoImageModal(false)}
        >
          <div className="promo-image-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="promo-modal-close"
              onClick={() => setShowPromoImageModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <img
              src={dashboard.store_promotion.image_url}
              alt={dashboard.store_promotion.title}
              className="promo-modal-image"
            />
            <div className="promo-modal-caption">
              <strong>{dashboard.store_promotion.title}</strong>
            </div>
          </div>
        </div>
      )}
    </BridgeClubLayout>
  );
};

export default BridgeClubDashboard;
